import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import { toast } from 'sonner';
import { Mail, Lock, User, ArrowLeft, UserPlus, ChevronDown, Loader2, Music, Phone } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function RegisterPage() {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [currentUser, setCurrentUser] = useState(null);
  
  // Admin artist registration
  const [nonArtistUsers, setNonArtistUsers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);
  const [showAdminRegister, setShowAdminRegister] = useState(false);
  
  const navigate = useNavigate();

  useEffect(() => {
    checkCurrentUser();
  }, []);

  const checkCurrentUser = async () => {
    try {
      const response = await fetch(`${API_URL}/api/auth/me`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setCurrentUser(data);
        if (data.is_admin) {
          fetchNonArtistUsers();
        }
      }
    } catch (error) {
      console.error('Failed to check user');
    }
  };

  const fetchNonArtistUsers = async () => {
    setIsLoadingUsers(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/non-artist-users`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setNonArtistUsers(data);
      }
    } catch (error) {
      console.error('Failed to fetch users');
    } finally {
      setIsLoadingUsers(false);
    }
  };

  const isValidEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const clearError = (field) => {
    setErrors(prev => ({ ...prev, [field]: '' }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Clear previous errors
    setErrors({});
    let hasErrors = false;
    const newErrors = {};
    
    // Validate name
    if (!name.trim()) {
      newErrors.name = 'Full name is required';
      hasErrors = true;
    }
    
    // Validate email
    if (!email) {
      newErrors.email = 'Email is required';
      hasErrors = true;
    } else if (!isValidEmail(email)) {
      newErrors.email = 'Not valid email';
      hasErrors = true;
    }
    
    // Validate password
    if (!password) {
      newErrors.password = 'Password is required';
      hasErrors = true;
    }
    
    // Validate phone
    if (!phone.trim()) {
      newErrors.phone = 'Phone number is required';
      hasErrors = true;
    } else if (phone.trim().length < 10) {
      newErrors.phone = 'Enter a valid phone number';
      hasErrors = true;
    }
    
    if (hasErrors) {
      setErrors(newErrors);
      return;
    }
    
    setIsLoading(true);

    try {
      const res = await fetch(`${API_URL}/api/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          name: name.trim(), 
          email: email,
          phone: phone.trim(), 
          password 
        })
      });

      const data = await res.json();

      if (res.ok) {
        toast.success('Registration successful! Please sign in.');
        navigate('/login', { replace: true });
      } else {
        // Map backend errors to specific fields
        const errorMsg = data.detail || 'Registration failed';
        if (errorMsg.toLowerCase().includes('email')) {
          setErrors({ email: errorMsg });
        } else if (errorMsg.toLowerCase().includes('name')) {
          setErrors({ name: errorMsg });
        } else if (errorMsg.toLowerCase().includes('number') || errorMsg.toLowerCase().includes('phone')) {
          setErrors({ phone: errorMsg });
        } else {
          setErrors({ general: errorMsg });
        }
      }
    } catch (error) {
      setErrors({ general: 'Connection failed. Please try again.' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleAdminRegisterArtist = async () => {
    if (!selectedUserId) {
      toast.error('Please select a user');
      return;
    }

    setIsLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/register-artist/${selectedUserId}`, {
        method: 'POST',
        credentials: 'include'
      });

      const data = await response.json();

      if (response.ok) {
        toast.success('User registered as artist successfully!');
        setSelectedUserId('');
        fetchNonArtistUsers();
        navigate(`/Sign-up?userId=${selectedUserId}`);
      } else {
        toast.error(data.detail || 'Failed to register as artist');
      }
    } catch (error) {
      toast.error('Failed to register as artist');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-12">
      <Card className="glass-panel w-full max-w-md p-8">
        {/* Back Button */}
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate('/')}
          className="mb-4 -ml-2"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Home
        </Button>

        <div className="text-center mb-6">
          <h1 className="text-4xl font-bold mb-2 tracking-tight">Join Tsarona</h1>
          <p className="text-muted-foreground">Create your account and start your journey</p>
        </div>

        {/* Google Sign Up - Primary CTA */}
        <Button
          data-testid="google-register-btn"
          type="button"
          className="w-full bg-white hover:bg-gray-100 text-gray-800 rounded-full py-6 font-bold text-base shadow-lg shadow-white/10 mb-6"
          onClick={() => {
            const redirectUrl = window.location.origin + '/dashboard';
            window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
          }}
        >
          <svg className="mr-3 h-5 w-5" viewBox="0 0 24 24">
            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
          </svg>
          Sign up with Google
        </Button>

        <div className="relative mb-6">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-white/10"></div>
          </div>
          <div className="relative flex justify-center text-sm">
            <span className="px-4 bg-card text-muted-foreground">Or register with email</span>
          </div>
        </div>

        {/* Admin: Register Existing User as Artist */}
        {currentUser?.is_admin && (
          <div className="mb-6">
            <Button
              variant="outline"
              onClick={() => setShowAdminRegister(!showAdminRegister)}
              className="w-full rounded-xl border-purple-500/50 text-purple-400 hover:bg-purple-500/10 mb-4"
            >
              <Music className="mr-2 h-4 w-4" />
              Admin: Register User as Artist
              <ChevronDown className={`ml-auto h-4 w-4 transition-transform ${showAdminRegister ? 'rotate-180' : ''}`} />
            </Button>

            {showAdminRegister && (
              <Card className="bg-purple-900/30 border-purple-500/30 p-4 rounded-xl mb-6">
                <h3 className="font-bold text-sm mb-3 text-purple-300">Select a registered user to make them an artist:</h3>
                
                {isLoadingUsers ? (
                  <div className="flex items-center justify-center py-4">
                    <Loader2 className="h-6 w-6 animate-spin text-purple-400" />
                  </div>
                ) : nonArtistUsers.length === 0 ? (
                  <p className="text-sm text-gray-400 py-4 text-center">
                    No eligible users found. All users are either already artists or admins.
                  </p>
                ) : (
                  <>
                    <select
                      value={selectedUserId}
                      onChange={(e) => setSelectedUserId(e.target.value)}
                      className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-3 text-white mb-3 focus:outline-none focus:border-purple-500"
                      data-testid="admin-user-select"
                    >
                      <option value="">-- Select a user --</option>
                      {nonArtistUsers.map((user) => (
                        <option key={user.user_id} value={user.user_id}>
                          {user.name} ({user.email})
                        </option>
                      ))}
                    </select>

                    <Button
                      onClick={handleAdminRegisterArtist}
                      disabled={!selectedUserId || isLoading}
                      className="w-full bg-purple-600 hover:bg-purple-700 rounded-full"
                      data-testid="admin-register-artist-btn"
                    >
                      {isLoading ? (
                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      ) : (
                        <UserPlus className="h-4 w-4 mr-2" />
                      )}
                      Register as Artist
                    </Button>
                  </>
                )}
              </Card>
            )}
          </div>
        )}

        {/* General Error */}
        {errors.general && (
          <div className="mb-4 p-3 bg-red-500/10 border border-red-500/50 rounded-xl">
            <p className="text-red-500 text-sm">{errors.general}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className="text-sm font-medium mb-2 block">Full Name *</label>
            <div className="relative">
              <User className="absolute left-3 top-3 h-5 w-5 text-muted-foreground" />
              <Input
                data-testid="register-name-input"
                type="text"
                value={name}
                onChange={(e) => { setName(e.target.value); clearError('name'); }}
                className={`pl-10 bg-white/5 border-white/10 focus:border-primary h-12 rounded-xl ${errors.name ? 'border-red-500' : ''}`}
                placeholder="John Doe"
              />
            </div>
            {errors.name && <p className="text-red-500 text-sm mt-1">{errors.name}</p>}
          </div>

          <div>
            <label className="text-sm font-medium mb-2 block">Email *</label>
            <div className="relative">
              <Mail className="absolute left-3 top-3 h-5 w-5 text-muted-foreground" />
              <Input
                data-testid="register-email-input"
                type="email"
                value={email}
                onChange={(e) => { setEmail(e.target.value); clearError('email'); }}
                className={`pl-10 bg-white/5 border-white/10 focus:border-primary h-12 rounded-xl ${errors.email ? 'border-red-500' : ''}`}
                placeholder="email@example.com"
              />
            </div>
            {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email}</p>}
          </div>

          <div>
            <label className="text-sm font-medium mb-2 block">Phone Number *</label>
            <div className="relative">
              <Phone className="absolute left-3 top-3 h-5 w-5 text-muted-foreground" />
              <Input
                data-testid="register-phone-input"
                type="tel"
                value={phone}
                onChange={(e) => { setPhone(e.target.value); clearError('phone'); }}
                className={`pl-10 bg-white/5 border-white/10 focus:border-primary h-12 rounded-xl ${errors.phone ? 'border-red-500' : ''}`}
                placeholder="0712345678"
              />
            </div>
            {errors.phone && <p className="text-red-500 text-sm mt-1">{errors.phone}</p>}
          </div>

          <div>
            <label className="text-sm font-medium mb-2 block">Password *</label>
            <div className="relative">
              <Lock className="absolute left-3 top-3 h-5 w-5 text-muted-foreground" />
              <Input
                data-testid="register-password-input"
                type="password"
                value={password}
                onChange={(e) => { setPassword(e.target.value); clearError('password'); }}
                className={`pl-10 bg-white/5 border-white/10 focus:border-primary h-12 rounded-xl ${errors.password ? 'border-red-500' : ''}`}
                placeholder="••••••••"
              />
            </div>
            {errors.password && <p className="text-red-500 text-sm mt-1">{errors.password}</p>}
          </div>

          <Button
            data-testid="register-submit-btn"
            type="submit"
            className="w-full bg-primary hover:scale-105 transition-transform rounded-full py-6 font-bold"
            disabled={isLoading}
          >
            {isLoading ? 'Creating Account...' : 'Create Account'}
          </Button>
        </form>

        <p className="text-center text-sm text-muted-foreground mt-6">
          Already have an account?{' '}
          <Link to="/login" className="text-primary hover:underline font-medium">
            Sign in
          </Link>
        </p>
      </Card>
    </div>
  );
}

export default RegisterPage;
